import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { MaskedInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { JURSIDICTIONS, STATES } from "../shared/IntakeConstants";
import type { FormData } from "../shared/IntakeTypes";

type AccidentInfoIntakeFormProps = {
  handleFieldChange: (
    field: string,
    value: string | number | boolean | Array<{ value: string; label: string }>,
  ) => Promise<void>;
  doiIndex?: number;
  // Ticket requirement (KAB-3359), non-negotiable: Claim Notes must render
  // beside Accident Information on intake, not on another page. Do not
  // remove this prop or move Claim Notes elsewhere (e.g. Claimant
  // Information) to work around the fact that this component only mounts
  // when hasMeaningfulDOI() is true -- that tradeoff (unreachable via
  // "Skip & Submit" before any DOI has data, same as accident_description
  // itself) has been explicitly accepted in favor of this placement.
  isLibertyUser?: boolean;
};

const AccidentInfoIntakeForm = ({
  handleFieldChange,
  doiIndex,
  isLibertyUser,
}: AccidentInfoIntakeFormProps) => {
  const {
    control,
    setValue,
    getValues,
    watch,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useFormContext<FormData>();

  const datesOfInjury = getValues("dates_of_injury") || [];
  const selectedServices = watch("services") || [];

  // use provided doiIndex or default to 0 for backward compatibility
  const injuryIndex = doiIndex ?? 0;
  const currentDOI = datesOfInjury[injuryIndex];

  // check if any selected service requires WCMSA fields
  const requiresWCMSAFields = useMemo(() => {
    const msaServiceTypes = ["MSA", "MSA-111", "PRE-MSA", "ARV"];
    return selectedServices.some((service: { value: string; label: string }) =>
      msaServiceTypes.includes(service.value),
    );
  }, [selectedServices]);

  // check if Section-111 service is selected for 111 Jurisdiction field
  const requiresSection111Field = useMemo(() => {
    return selectedServices.some(
      (service: { value: string; label: string }) => service.value === "111",
    );
  }, [selectedServices]);

  const handleEmbeddedFieldChange = useCallback(
    async (
      field: string,
      value:
        | string
        | number
        | boolean
        | Array<{ value: string; label: string }>,
    ) => {
      await handleFieldChange(`dates_of_injury.${injuryIndex}.${field}`, value);
    },
    [injuryIndex, handleFieldChange],
  );

  useEffect(() => {
    // clear WCMSA field values and errors when not required
    if (!requiresWCMSAFields && datesOfInjury.length > 0) {
      datesOfInjury.forEach((_, index) => {
        // clear values from dates_of_injury
        setValue(`dates_of_injury.${index}.jurisdiction`, "");
        setValue(`dates_of_injury.${index}.wcmsa_zip_code`, "");
      });
    }

    // clear 111 Jurisdiction field values and errors when not required
    if (!requiresSection111Field && datesOfInjury.length > 0) {
      datesOfInjury.forEach((_, index) => {
        // clear values from dates_of_injury
        setValue(`dates_of_injury.${index}.one_eleven_jurisdiction`, "");
      });
    }
  }, [
    isSubmitting,
    datesOfInjury,
    setValue,
    requiresWCMSAFields,
    requiresSection111Field,
    clearErrors,
  ]);

  // create a dynamic heading based on DOI index
  const cardHeading =
    doiIndex !== undefined
      ? `Accident Information - Date of Injury ${doiIndex + 1}${currentDOI?.date ? ` (${currentDOI.date})` : ""}`
      : "Accident Information";

  // Reused below for the Claim Notes card too -- cardNumber only feeds a
  // CSS class (card-${cardNumber}) in Card.tsx, not a visible step counter
  // or DOM ordering key, so multiple cards sharing one value is expected
  // for cards belonging to the same wizard step (same pattern already used
  // by EmployerIntakeForm.tsx's Employer/Insurer/TPA cards, all cardNumber={3}).
  const cardNumber = doiIndex !== undefined ? doiIndex + 4 : 4;

  return (
    <>
      <Card
        heading={cardHeading}
        cardNumber={cardNumber}
        style={{ columnSpan: isLibertyUser ? undefined : "all" }}
      >
        {requiresWCMSAFields && (
          <ControlledSelect<FormData>
            control={control}
            name={`dates_of_injury.${injuryIndex}.jurisdiction`}
            label="WCMSA State"
            errors={errors}
            options={STATES}
            readOnly={!currentDOI}
            onSave={async (option) => {
              if (option) {
                setValue("jurisdiction", String(option.value));
                await handleEmbeddedFieldChange("jurisdiction", option.value);
              }
            }}
          />
        )}

        {requiresWCMSAFields && (
          <ControlledInput
            control={control}
            name={`dates_of_injury.${injuryIndex}.wcmsa_zip_code`}
            label="WCMSA Zip Code"
            errors={errors}
            maxLength={11}
            onSave={(value) =>
              handleEmbeddedFieldChange("wcmsa_zip_code", value)
            }
            componentType={MaskedInput}
            mask="zipcode"
            readOnly={!currentDOI}
          />
        )}

        {requiresSection111Field && (
          <ControlledSelect<FormData>
            control={control}
            name={`dates_of_injury.${injuryIndex}.one_eleven_jurisdiction`}
            label="111 Jurisdiction"
            errors={errors}
            options={JURSIDICTIONS}
            readOnly={!currentDOI}
            onSave={async (option) => {
              if (option) {
                setValue(
                  `dates_of_injury.${injuryIndex}.one_eleven_jurisdiction`,
                  String(option.value),
                );
                await handleEmbeddedFieldChange(
                  "one_eleven_jurisdiction",
                  option.value,
                );
              }
            }}
          />
        )}
        <ControlledTextArea<FormData>
          control={control}
          name={`dates_of_injury.${injuryIndex}.accident_description`}
          label="Accident Description (enter n/a if not applicable)"
          errors={errors}
          advancedEdit={true}
          onSave={(value) =>
            handleEmbeddedFieldChange("accident_description", value)
          }
          readOnly={!currentDOI}
        />
        <ControlledTextArea<FormData>
          control={control}
          name={`dates_of_injury.${injuryIndex}.description_of_claimants_job`}
          label="Description of Claimant's Job at time of injury (enter n/a if not applicable)"
          errors={errors}
          advancedEdit={true}
          onSave={(value) =>
            handleEmbeddedFieldChange("description_of_claimants_job", value)
          }
          readOnly={!currentDOI}
        />
        <ControlledTextArea<FormData>
          control={control}
          name={`dates_of_injury.${injuryIndex}.accepted_conditions`}
          label="Accepted Conditions (enter n/a if not applicable)"
          errors={errors}
          advancedEdit={true}
          onSave={(value) =>
            handleEmbeddedFieldChange("accepted_conditions", value)
          }
          readOnly={!currentDOI}
        />
        <ControlledTextArea<FormData>
          control={control}
          name={`dates_of_injury.${injuryIndex}.denied_conditions`}
          label="Denied Conditions (enter n/a if not applicable)"
          errors={errors}
          advancedEdit={true}
          onSave={(value) =>
            handleEmbeddedFieldChange("denied_conditions", value)
          }
          readOnly={!currentDOI}
        />
      </Card>
      {/* Ticket requirement (KAB-3359), non-negotiable: renders on every DOI
          page (not just the first), all bound to the same top-level
          claim_notes field -- the wizard mounts all pages simultaneously
          (display:none toggling, not unmount/remount), so multiple
          Controllers sharing this field name stay in sync automatically.
          This is deliberate: the same Claim Notes value/textarea shows next
          to Accident Information for every accident, not a per-DOI value. */}
      {isLibertyUser && (
        <Card heading="Claim Notes" cardNumber={cardNumber}>
          <ControlledTextArea<FormData>
            control={control}
            name="claim_notes"
            label={null}
            errors={errors}
            advancedEdit={true}
            onSave={(value) => handleFieldChange("claim_notes", value)}
          />
        </Card>
      )}
    </>
  );
};

export default AccidentInfoIntakeForm;
